/**
 * PixiJS 8 wheel engine — command-driven, no Zustand/React.
 * Scene created once, display objects reused.
 * Commands batched per frame, idempotent by roundVersion + command.id.
 */

import type { PixiCommand } from "@/entities/pvp-roulette/model/commands"
import {
  centidegreesToDegrees,
  getEffectiveTargetCoordinate,
  getEffectiveTiming,
  normalizeUnixTimestampToMs,
} from "@/shared/api/types/pvpRoulette"
import type { RoundSnapshot } from "@/shared/api/types/pvpRoulette"
import {
  TRICK_TYPE_FREEZE_WHEEL,
  TRICK_TYPE_PUSH_WHEEL,
} from "@/shared/api/types/pvpRoulette"
import {
  computeSlowingFinalizeDurationMs,
  mapSnapshotToWheelParties,
  resolveTricksEndsAtMs,
} from "@/entities/pvp-roulette/model/reduceRoomState"
import type {
  WheelDiagnosticsCallback,
  WheelPartyDiagSnapshot,
} from "./wheelDiagnosticsTypes"
import { getEasing, registerEasing, type EasingFn } from "./easing"
import { EASING_KEY } from "@/shared/constants/pvpRoulette"
import {
  applyTricksPhaseSectorColors,
  lerpSectorColor,
  partiesUseTricksPalette,
  userParticipatesInWheelParties,
  wheelPartiesColorsEqual,
} from "@/shared/lib/pvpWheelTricksColors"
import { getPvpRouletteRuntime } from "@/shared/lib/pvp-roulette-runtime/runtime"
import {
  solveFinalizeDelta,
  computeSlowingVelocity,
  makeCommandDedupKey,
} from "./wheelPhysics"
import { WheelPhysicsState } from "./wheelPhysicsState"
import {
  WheelSceneManager,
  computeSectorStartAngles,
} from "./WheelSceneManager"
import { TrixCharManager } from "./TrixCharManager"
import { WheelEffectsLayer } from "./WheelEffectsLayer"
import { safeStopPixiApplication } from "./pixiTeardown"

// Alias for clarity
type WheelCommand = PixiCommand
type EngineLifecyclePhase =
  | "IDLE"
  | "WAITING"
  | "BETTING"
  | "SPINNING"
  | "TRICKS"
  | "SLOWING"
  | "RESOLVED"

interface SectorParty {
  userId: string
  username?: string | null
  sectorId?: string
  percent: number
  betAmount: string
  color?: number
  partyType?: "SOLO" | "TEAM"
  /** Avatar image URL for the sector owner (SOLO) or fallback avatar (TEAM). */
  avatarUrl?: string | null
  /** Squad members for TEAM sectors — each may have their own avatarUrl. */
  members?: Array<{
    userId: string
    username?: string | null
    avatarUrl?: string | null
  }>
}

/** Replaceable clock — inject in tests to control time without fake timers. */
export interface WheelClock {
  /** Replaces performance.now() — high-resolution monotonic time (ms). */
  perfNow(): number
  /** Replaces Date.now() — wall-clock time (ms). Used only for TRICKS decay alignment. */
  wallNow(): number
}

/** Optional split mounts: back canvas (wheel) → DOM hub → front canvas (trick VFX). */
export interface WheelEngineMountLayers {
  back: HTMLElement
  front: HTMLElement
}

/** Store snapshot reconcile when the Pixi command queue is already drained (e.g. route return). */
export interface WheelRoomReconcileInput {
  snapshot: RoundSnapshot
  /** Skip when > 0 — pending `apply()` will drive updates. */
  pendingCommandsCount: number
  userId?: string
}

export interface WheelEngineOptions {
  easing?: Record<string, EasingFn>
  /** Min spin duration guard (ms) in case of invalid server timestamps. */
  minSpinDurationMs?: number
  /** Min extra full rotations during StartSpin. Emulator: 5. Production: 0 (min 180°). */
  minSpinRotations?: number
  /** Diagnostic callback (emulator only). */
  onDiagnostics?: WheelDiagnosticsCallback
  /** Override clock for testing. Default: performance.now / Date.now. */
  clock?: WheelClock
  /** When set, VFX canvas mounts on `front` (above DOM hub at z-10). */
  layers?: WheelEngineMountLayers
}

// Extra canvas space outside the wheel circle for the mascot to render without clipping.
// Increase these if the mascot body or head still gets cut off at the edges.
const MASCOT_CANVAS_PAD_LEFT = 100
const MASCOT_CANVAS_PAD_TOP = 150

// Visual offset (degrees, clockwise from 12 o'clock) of the mascot claw tip,
// measured from the static-pointer position. Subtracted from every server-supplied
// `targetAngleDeg` so the wheel finalizes with the winning sector under the claw
// while sectors / avatars / center overlay keep their natural orientation.
const WHEEL_POINTER_OFFSET_DEG = 11
function applyPointerOffset(deg: number): number {
  return deg - WHEEL_POINTER_OFFSET_DEG
}

export class WheelEngine {
  /** Pixi scene — owns Application, containers, graphics pool, avatars. */
  private scene: WheelSceneManager | null = null
  private containerEl: HTMLElement | null = null
  private initReady: Promise<void> | null = null
  private trixChar: TrixCharManager | null = null
  private effectsLayer: WheelEffectsLayer | null = null
  private backMountEl: HTMLElement | null = null
  private frontMountEl: HTMLElement | null = null
  /** All physics simulation state (SPINNING/TRICKS/SLOWING/trick overlays). */
  private readonly physics = new WheelPhysicsState()
  private parties: SectorParty[] = []
  private appliedCommandIds = new Set<string>()
  /** Snapshot `roundId` carried on PixiCommand as `roomId` (not Centrifugo channel id). */
  private lastDedupBackendRoundId: string | null = null
  private lastRoundVersion = 0
  /** Last `roundId:version` applied by `reconcileWithRoomSlice` (navigation / color-only sync). */
  private lastVisualSyncKey: string | null = null
  /** One push/freeze impulse per `${roundId}:${userId}:${type}` — server echo after optimistic must not stack. */
  private trickVisualEffectKeys = new Set<string>()

  private sectorTween: {
    fromAngles: number[]
    toAngles: number[]
    /** Target parties (geometry + final colors). */
    parties: SectorParty[]
    /** When set, colors are lerped per sector during the tween. */
    fromParties?: SectorParty[]
    durationMs: number
    easing: EasingFn
    startTime: number
  } | null = null

  private currentUserId: string | null = null

  private pendingCommands: WheelCommand[] = []
  private rafScheduled = false
  /** Pending `scheduleCommandFlush` RAF — must cancel on detach/destroy. */
  private commandFlushRafId: number | null = null
  /** Pending wheel `tick` RAF — cancel on detach/destroy to avoid post-teardown render. */
  private tickRafId: number | null = null
  private isTicking = false
  private minSpinDurationMs: number
  private minSpinRotations: number
  private onDiagnostics?: WheelDiagnosticsCallback
  private diagLastSpeedTs = 0
  private prevRotationDeg = 0
  private initTime = 0
  // Guards against overlapping async init() calls that can attach duplicate canvases.
  private initSeq = 0
  /** Set on destroy(); detach/init become no-ops until a new engine is constructed. */
  private tornDown = false

  isTornDown(): boolean {
    return this.tornDown
  }
  private lifecyclePhase: EngineLifecyclePhase = "IDLE"
  private clock: WheelClock = {
    perfNow: () => performance.now(),
    wallNow: () => Date.now(),
  }

  async init(
    containerEl: HTMLElement,
    opts?: WheelEngineOptions
  ): Promise<void> {
    if (this.tornDown) return
    const seq = ++this.initSeq
    if (opts?.clock) this.clock = opts.clock
    this.initTime = this.clock.perfNow()
    this.containerEl = containerEl
    this.backMountEl = opts?.layers?.back ?? containerEl
    this.frontMountEl = opts?.layers?.front ?? containerEl
    this.minSpinDurationMs =
      opts?.minSpinDurationMs ??
      getPvpRouletteRuntime().SPINNING_RAMP_UP_MS + 100
    this.minSpinRotations = opts?.minSpinRotations ?? 0
    this.onDiagnostics = import.meta.env.DEV ? opts?.onDiagnostics : undefined

    const rt = getPvpRouletteRuntime()
    const size = Math.min(
      containerEl.clientWidth || rt.WHEEL_DEFAULT_SIZE,
      rt.WHEEL_MAX_SIZE
    )
    const outerRadius = size * rt.WHEEL_OUTER_RADIUS_RATIO
    const innerRadius = size * rt.WHEEL_INNER_RADIUS_RATIO

    if (!this.scene) {
      this.scene = new WheelSceneManager(this.onDiagnostics)
    }

    // Fast path: live app + containers — resize, re-attach canvases (detach/remount, not destroy).
    if (this.canUseInitFastPath()) {
      this.syncLayoutToContainer()
      await this.ensureEffectsLayer(size)
      if (seq !== this.initSeq || !this.canUseInitFastPath()) return
      this.attachPixiCanvases()
      this.scene?.app?.start()
      this.effectsLayer?.start()
      this.emitWheelScene("init_resize_remount")
      this.scheduleCommandFlush()
      return
    }

    // Aborted overlapping init can leave containers pointing at a destroyed Application.
    if (
      this.scene.wheelContainer &&
      this.scene.slicesContainer &&
      !this.scene.app?.renderer
    ) {
      this.scene.destroy()
    }

    // Fresh init path — `settle` must run on success or abort so `apply()`'s initReady.then is never orphaned (Strict Mode / overlapping init).
    let settle: (() => void) | null = null
    this.initReady = new Promise<void>((resolve) => {
      settle = () => {
        if (!settle) return
        settle = null
        resolve()
      }
    })
    if (opts?.easing) {
      Object.entries(opts.easing).forEach(([k, fn]) => registerEasing(k, fn))
    }

    const app = await this.scene.createApp(
      size,
      outerRadius,
      innerRadius,
      MASCOT_CANVAS_PAD_LEFT,
      MASCOT_CANVAS_PAD_TOP
    )

    if (seq !== this.initSeq) {
      app.destroy(true, { children: true })
      settle?.()
      return
    }

    this.scene.setupContainers(app, containerEl)

    if (!this.trixChar) {
      this.trixChar = new TrixCharManager()
    }
    app.stage.addChildAt(this.trixChar.containerBack, 0)

    if (!this.effectsLayer) {
      this.effectsLayer = new WheelEffectsLayer()
    }
    const effectsApp = await this.effectsLayer.create(
      size,
      MASCOT_CANVAS_PAD_LEFT,
      MASCOT_CANVAS_PAD_TOP
    )
    if (seq !== this.initSeq) {
      // Do not destroy `app` — a newer init may already use this.scene via fast path.
      if (this.effectsLayer?.app === effectsApp) {
        this.effectsLayer.destroy()
        this.effectsLayer = null
      }
      settle?.()
      return
    }
    this.effectsLayer.mountRoot(this.trixChar.containerFront)
    this.effectsLayer.start()

    const wheelCenterX = size / 2 + MASCOT_CANVAS_PAD_LEFT
    const wheelCenterY = size / 2 + MASCOT_CANVAS_PAD_TOP
    this.trixChar.load(wheelCenterX, wheelCenterY, outerRadius).catch((err) => {
      console.error("[WheelEngine] TrixCharManager load failed:", err)
    })

    this.attachPixiCanvases()

    settle?.()
    app.start()
    this.emitWheelScene("init_fresh_canvas")
    this.scheduleCommandFlush()
  }

  /**
   * Re-sync Pixi geometry to the DOM mount (`clientWidth` → renderer size / radii / mascot).
   * Called on init remount and when the layout box changes after mount (viewport, scrollbar, fullscreen).
   */
  syncLayoutToContainer(): void {
    if (
      !this.scene?.app ||
      !this.containerEl ||
      !this.scene?.wheelContainer ||
      !this.scene?.slicesContainer ||
      !this.scene?.staticContainer
    )
      return
    const rt = getPvpRouletteRuntime()
    const size = Math.min(
      this.containerEl.clientWidth || rt.WHEEL_DEFAULT_SIZE,
      rt.WHEEL_MAX_SIZE
    )
    const outerRadius = size * rt.WHEEL_OUTER_RADIUS_RATIO
    const innerRadius = size * rt.WHEEL_INNER_RADIUS_RATIO
    this.scene.resize(this.containerEl, size, outerRadius, innerRadius)
    if (this.sectorTween) {
      // `drawSectors` resets to endpoint layout and would snap mid-tween; reuse ticker math.
      this.tickSectorTween(this.clock.perfNow())
    } else {
      this.scene.drawSectors(this.parties)
    }
    this.scene.drawCenter()
    this.scene.drawPointer()
    this.trixChar?.reposition(
      size / 2 + MASCOT_CANVAS_PAD_LEFT,
      size / 2 + MASCOT_CANVAS_PAD_TOP,
      outerRadius
    )
    this.effectsLayer?.resize(
      size,
      MASCOT_CANVAS_PAD_LEFT,
      MASCOT_CANVAS_PAD_TOP
    )
    // Intentionally no `app.render()` — app ticker already paints; manual render here raced batching.
  }

  /**
   * После смены `getPvpRouletteRuntime()` (dev-эмулятор): пересчитать размер/radius и перерисовать сцену.
   */
  public reapplyRuntimeLayout(): void {
    this.syncLayoutToContainer()
  }

  /**
   * Detach canvas from the current container without destroying the engine.
   * The Pixi Application and scene stay alive; next init() call will re-attach.
   *
   * Resets wheel domain state so the next mount does not paint the previous room's
   * `this.parties` in init() fast-path (`drawSectors(this.parties)`) or replay dedup wrong.
   */
  public detach(): void {
    if (this.tornDown) return
    // Drain Pixi commands before dropping the canvas so pending work is not lost between
    // `apply` (same tick as `ackCommands` in React) and the next animation frame.
    this.cancelEngineAnimationFrames()
    if (
      this.scene?.wheelContainer &&
      this.scene?.slicesContainer &&
      this.pendingCommands.length > 0
    ) {
      this.flushCommands()
    }
    this.pendingCommands = []

    safeStopPixiApplication(this.effectsLayer?.app ?? null)
    safeStopPixiApplication(this.scene?.app ?? null)
    this.effectsLayer?.detach()

    try {
      this.scene?.app?.canvas?.remove()
    } catch {
      // Canvas may already be detached.
    }
    this.containerEl = null
    this.backMountEl = null
    this.frontMountEl = null
    const preservedVisualDeg =
      this.physics.currentRotationDeg + this.physics.trickPermanentOffsetDeg
    this.trixChar?.pauseRouteDetachEffects()
    this.parties = []
    this.sectorTween = null
    this.physics.pauseForNavigationDetach(preservedVisualDeg)
    this.scene?.resetDisplay(preservedVisualDeg)
    this.lastVisualSyncKey = null
    // Keep dedup + appliedCommandIds so returning from another route does not replay the whole round.
  }

  destroy(): void {
    if (this.tornDown) return
    this.tornDown = true
    this.initSeq += 1
    this.cancelEngineAnimationFrames()
    this.sectorTween = null
    this.pendingCommands = []
    this.rafScheduled = false

    safeStopPixiApplication(this.effectsLayer?.app ?? null)
    safeStopPixiApplication(this.scene?.app ?? null)

    this.trixChar?.destroy()
    this.trixChar = null

    this.effectsLayer?.destroy()
    this.effectsLayer = null

    this.scene?.destroy()
    this.scene = null

    this.physics.resetAll()
    this.containerEl = null
    this.backMountEl = null
    this.frontMountEl = null
  }

  /**
   * Set the current user's ID so the engine can highlight their solo sector.
   * Safe to call before or after init; triggers a redraw if the scene is ready.
   */
  public setCurrentUserId(id: string | null): void {
    this.currentUserId = id
    this.scene?.setCurrentUserId(id, this.parties)
  }

  /**
   * Load a tiling texture for team sector backgrounds (data URL or regular URL).
   * Null clears the pattern — team sectors fall back to solid TEAM_1_COLOR.
   * Safe to call before or after init; triggers a redraw if the scene is ready.
   */
  public async setTeamPatternSrc(src: string | null): Promise<void> {
    if (!this.scene) return
    try {
      await this.scene.loadTeamPatternTexture(src)
      // SOLO rounds never need a full pool rebuild here; doing so mid–sector tween could
      // fight `drawSectorsFromAngles`. TEAM sectors need a repaint once the tile exists.
      if (
        this.scene.slicesContainer &&
        this.parties.length > 0 &&
        this.parties.some((p) => p.partyType === "TEAM") &&
        this.sectorTween == null
      ) {
        this.scene.drawSectors(this.parties)
      }
    } catch (e) {
      console.warn("[WheelEngine] Failed to load team pattern texture:", e)
    }
  }

  /**
   * Apply commands. Batched per frame, idempotent by snapshot roundId + roundVersion + command.id.
   * Dedup set clears when backend round id changes or when version moves up — or **down** (fixture replay).
   */
  apply(commands: WheelCommand[]): void {
    if (this.tornDown || !commands.length) return
    this.pendingCommands.push(...commands)
    void this.initReady?.then(() => this.scheduleCommandFlush())
    // Immediate schedule too: first flush may re-queue until Pixi containers exist; after settle() another flush drains pending.
    this.scheduleCommandFlush()
    // Same-tick drain when the scene already exists so React+Zustand cannot `ack` the store queue
    // and `detach` clear pending before the scheduled RAF runs (sectors would never update).
    if (this.scene?.wheelContainer && this.scene?.slicesContainer) {
      if (this.commandFlushRafId !== null) {
        cancelAnimationFrame(this.commandFlushRafId)
        this.commandFlushRafId = null
      }
      this.rafScheduled = false
      this.flushCommands()
    }
  }

  private cancelEngineAnimationFrames(): void {
    if (this.commandFlushRafId !== null) {
      cancelAnimationFrame(this.commandFlushRafId)
      this.commandFlushRafId = null
    }
    if (this.tickRafId !== null) {
      cancelAnimationFrame(this.tickRafId)
      this.tickRafId = null
    }
    this.rafScheduled = false
  }

  /** True when detach() left a live Pixi scene that only needs canvas re-attach. */
  private canUseInitFastPath(): boolean {
    const scene = this.scene
    return !!(
      scene?.app?.renderer &&
      scene.wheelContainer &&
      scene.slicesContainer &&
      scene.staticContainer
    )
  }

  private async ensureEffectsLayer(size: number): Promise<void> {
    if (!this.trixChar) {
      this.trixChar = new TrixCharManager()
    }
    if (!this.effectsLayer) {
      this.effectsLayer = new WheelEffectsLayer()
    }
    const app = await this.effectsLayer.create(
      size,
      MASCOT_CANVAS_PAD_LEFT,
      MASCOT_CANVAS_PAD_TOP
    )
    this.effectsLayer.mountRoot(this.trixChar.containerFront)
    if (this.scene?.app) {
      const front = this.trixChar.containerFront
      if (front.parent === this.scene.app.stage) {
        this.scene.app.stage.removeChild(front)
      }
    }
    if (this.trixChar.containerFront.parent !== app.stage) {
      this.effectsLayer.mountRoot(this.trixChar.containerFront)
    }
    if (this.scene?.app && this.trixChar.containerBack.parent !== this.scene.app.stage) {
      this.scene.app.stage.addChildAt(this.trixChar.containerBack, 0)
    }
    this.effectsLayer.start()
  }

  private attachPixiCanvases(): void {
    const backMount = this.backMountEl ?? this.containerEl
    const frontMount = this.frontMountEl ?? this.containerEl
    const backCanvas = this.scene?.app?.canvas
    const frontCanvas = this.effectsLayer?.app?.canvas
    if (!backMount || !backCanvas) return

    const sameMount = backMount === frontMount
    const backZ = sameMount ? 0 : undefined
    const frontZ = sameMount ? 20 : 20

    if (backCanvas.parentElement !== backMount) {
      backCanvas.remove()
      backMount.appendChild(backCanvas)
    }
    if (sameMount && backZ !== undefined) {
      backCanvas.style.zIndex = String(backZ)
    }

    if (frontMount && frontCanvas) {
      this.effectsLayer?.attachTo(frontMount, frontZ)
    }

    const keep = new Set([backCanvas, frontCanvas].filter(Boolean))
    for (const mount of new Set([backMount, frontMount])) {
      if (!mount) continue
      for (const canvas of Array.from(mount.querySelectorAll("canvas"))) {
        if (!keep.has(canvas)) canvas.remove()
      }
    }
  }

  /**
   * Manual `Application#render` is guarded: the default app ticker also renders every frame, and
   * a nested/extra pass (e.g. ResizeObserver rAF during layout) can leave Pixi v8's batcher with a
   * null `textureBatch` (`Batcher.break`). Skip when detached or renderer is already torn down.
   */
  private tryRenderApp(): void {
    if (this.tornDown || !this.containerEl) return
    const app = this.scene?.app
    if (!app?.renderer) return
    try {
      app.render()
    } catch {
      // Ignore — rare race with app ticker teardown or GL batch state.
    }
  }

  private scheduleCommandFlush(): void {
    if (this.rafScheduled) return
    this.rafScheduled = true
    this.commandFlushRafId = requestAnimationFrame(() => {
      this.commandFlushRafId = null
      this.flushCommands()
    })
  }

  private scheduleWheelTick(): void {
    if (this.tickRafId !== null) {
      cancelAnimationFrame(this.tickRafId)
    }
    this.tickRafId = requestAnimationFrame(() => {
      this.tickRafId = null
      this.tick()
    })
  }

  private flushCommands(): void {
    if (this.tornDown) return
    this.rafScheduled = false
    const batch = this.pendingCommands
    this.pendingCommands = []
    if (!this.scene?.wheelContainer || !this.scene?.slicesContainer) {
      if (batch.length > 0) {
        this.pendingCommands = batch.concat(this.pendingCommands)
        this.emitWheelScene("flush_requeue_no_scene", {
          partiesArg: this.parties.length,
        })
      }
      return
    }

    let skippedDuplicate = 0
    const cmdTypes: string[] = []
    for (const cmd of batch) {
      if (this.isAlreadyApplied(cmd)) {
        skippedDuplicate++
        continue
      }
      this.executeCommand(cmd)
      this.markApplied(cmd)
      cmdTypes.push(cmd.type)
    }
    if (cmdTypes.length > 0) {
      this.tryRenderApp()
    }
    if (this.onDiagnostics && batch.length > 0) {
      this.onDiagnostics({
        type: "command_batch",
        ts: this.clock.perfNow() - this.initTime,
        batchSize: batch.length,
        appliedCount: cmdTypes.length,
        skippedDuplicate,
        cmdTypes,
      })
      this.emitWheelScene(
        cmdTypes.length > 0
          ? `flush_after_${cmdTypes[0]}`
          : "flush_after_all_dup",
        {
          partiesArg: this.parties.length,
          ...(cmdTypes.length > 0 ? { forcedRender: true } : {}),
        }
      )
    }
  }

  /** Dedupe key must include backend round id (`cmd.roomId` on PixiCommand) — not only `roundVersion`. */
  private commandDedupKey(cmd: WheelCommand): string {
    return makeCommandDedupKey(cmd.roomId, cmd.roundVersion, cmd.id)
  }

  private isAlreadyApplied(cmd: WheelCommand): boolean {
    return this.appliedCommandIds.has(this.commandDedupKey(cmd))
  }

  private markApplied(cmd: WheelCommand): void {
    const backendRoundId = cmd.roomId

    if (this.lastDedupBackendRoundId !== backendRoundId) {
      this.lastDedupBackendRoundId = backendRoundId
      this.lastRoundVersion = cmd.roundVersion
      this.appliedCommandIds.clear()
    } else if (cmd.roundVersion > this.lastRoundVersion) {
      this.lastRoundVersion = cmd.roundVersion
      this.appliedCommandIds.clear()
    } else if (cmd.roundVersion < this.lastRoundVersion) {
      // Same captured roundId, version restarted (dev fixture replay / ingest after reset).
      this.lastRoundVersion = cmd.roundVersion
      this.appliedCommandIds.clear()
    }

    this.appliedCommandIds.add(this.commandDedupKey(cmd))
  }

  /**
   * Reserves the one-time push/freeze impulse for `roomId` + actor trick key (optimistic vs WS echo).
   * @returns whether to run the full impulse; `false` → `doApplyTrick*` only syncs `tricksEndsAt`.
   */
  private claimTrickVisualEffect(
    roomId: string,
    trickEffectKey?: string
  ): boolean {
    if (trickEffectKey == null || trickEffectKey.length === 0) return true
    const key = `${roomId}:${trickEffectKey}`
    if (this.trickVisualEffectKeys.has(key)) return false
    this.trickVisualEffectKeys.add(key)
    return true
  }

  private emitPhase(label: string): void {
    if (!this.onDiagnostics) return
    const ts = this.clock.perfNow() - this.initTime
    this.onDiagnostics({
      type: "phase",
      ts,
      phase: label,
      allowIdleSpin: this.physics.allowIdleSpin,
      isSpinningPhase: this.physics.isSpinningPhase,
      spinAngularVelDegPerMs: this.physics.spinAngularVelDegPerMs,
      spinAnimation: this.physics.finalizeState != null,
      trickSpeedBoost: this.physics.trickSpeedBoost != null,
      roundResolved: this.physics.roundResolved,
    })
  }

  private emitSectorWeightsTweenDiag(args: {
    durationMs: number
    easingKey: string
    fromPartyCount: number
    toPartyCount: number
    fromAnglesDeg: number[]
    toAnglesDeg: number[]
    normalizedFromDeg: number[]
    normalizedToDeg: number[]
    lerpPadLen: number
    toParties: SectorParty[]
  }): void {
    if (!this.onDiagnostics) return
    const toParties: WheelPartyDiagSnapshot[] = args.toParties.map((p) => ({
      userId: p.userId,
      sectorId: p.sectorId,
      partyType: p.partyType,
      percent:
        typeof p.percent === "number" ? p.percent : Number(p.percent) || 0,
    }))
    this.onDiagnostics({
      type: "sector_weights_tween",
      ts: this.clock.perfNow() - this.initTime,
      durationMs: args.durationMs,
      easingKey: args.easingKey,
      fromPartyCount: args.fromPartyCount,
      toPartyCount: args.toPartyCount,
      fromAnglesDeg: args.fromAnglesDeg.map((a) => Number(a.toFixed(4))),
      toAnglesDeg: args.toAnglesDeg.map((a) => Number(a.toFixed(4))),
      normalizedFromDeg: args.normalizedFromDeg.map((a) =>
        Number(a.toFixed(4))
      ),
      normalizedToDeg: args.normalizedToDeg.map((a) => Number(a.toFixed(4))),
      lerpPadLen: args.lerpPadLen,
      toParties,
    })
  }

  /** DOM + Pixi container snapshot (emulator diagnostics). */
  private emitWheelScene(
    tag: string,
    opts?: { partiesArg?: number; forcedRender?: boolean }
  ): void {
    if (!this.onDiagnostics) return
    const canvas = this.scene?.app?.canvas
    const cont = this.containerEl
    let canvasCssW = 0
    let canvasCssH = 0
    if (canvas) {
      const r = canvas.getBoundingClientRect()
      canvasCssW = r.width
      canvasCssH = r.height
    }
    const wheel = this.scene?.wheelContainer
    const slices = this.scene?.slicesContainer
    this.onDiagnostics({
      type: "wheel_scene",
      ts: this.clock.perfNow() - this.initTime,
      tag,
      canvasInDom: !!canvas?.isConnected,
      canvasCssW,
      canvasCssH,
      containerW: cont?.clientWidth ?? 0,
      containerH: cont?.clientHeight ?? 0,
      sizeCfg: this.scene?.size ?? 0,
      outerR: this.scene?.outerRadius ?? 0,
      innerR: this.scene?.innerRadius ?? 0,
      hasWheel: wheel != null,
      hasSlices: slices != null,
      slicesChildren: slices?.children.length ?? 0,
      poolSize: 0,
      partiesArg: opts?.partiesArg ?? -1,
      thisPartiesLen: this.parties.length,
      wheelVisible: wheel?.visible ?? false,
      slicesVisible: slices?.visible ?? false,
      wheelAlpha: wheel?.alpha ?? 0,
      slicesAlpha: slices?.alpha ?? 0,
      ...(opts?.forcedRender ? { forcedRender: true } : {}),
    })
  }

  private executeCommand(cmd: WheelCommand): void {
    if (this.onDiagnostics) {
      const ts = this.clock.perfNow() - this.initTime
      this.onDiagnostics({
        type: "command",
        ts,
        cmdType: cmd.type,
        payload: cmd.payload as Record<string, unknown>,
      })
    }
    switch (cmd.type) {
      case "InitWheelScene":
        this.doInitWheelScene(
          cmd.payload.parties,
          cmd.payload.status,
          undefined
        )
        break
      case "UpdateSectorWeightsTween":
        this.doUpdateSectorWeightsTween(
          cmd.payload.parties,
          cmd.payload.durationMs ?? 200,
          EASING_KEY.UI_FAST,
          cmd.payload.status
        )
        break
      case "EnterTricksPhase":
        this.doEnterTricksPhase(
          cmd.payload.tricksEndsAt,
          applyPointerOffset(cmd.payload.targetAngleDeg),
          cmd.payload.durationMs
        )
        break
      case "StartSpin":
        this.doStartSpin(
          applyPointerOffset(cmd.payload.targetAngleDeg),
          cmd.payload.easingKey ?? EASING_KEY.SPIN_IN,
          cmd.payload.spinningEndsAt,
          cmd.payload.durationMs
        )
        break
      case "ApplyTrickPush":
        this.doApplyTrickPush(
          cmd.payload.tricksEndsAt,
          this.claimTrickVisualEffect(cmd.roomId, cmd.payload.trickEffectKey)
        )
        break
      case "ApplyTrickFreeze":
        this.doApplyTrickFreeze(
          cmd.payload.tricksEndsAt,
          this.claimTrickVisualEffect(cmd.roomId, cmd.payload.trickEffectKey)
        )
        break
      case "SyncPlannedFinalizeTarget":
        this.doSyncPlannedFinalizeTarget(applyPointerOffset(cmd.payload.targetAngleDeg))
        break
      case "FinalizeToTarget": {
        const d = cmd.payload.durationMs
        const durationOk = d != null && Number.isFinite(d) && d > 0
        if (durationOk) {
          this.physics.scheduledFinalizeDurationMs = d
        } else {
          this.physics.scheduledFinalizeDurationMs = null
        }
        const adjustedTarget = applyPointerOffset(cmd.payload.targetAngleDeg)
        if (this.physics.isFinalizing && durationOk) {
          // Tick-scheduler already fired with fallback duration (~16ms ago).
          // Recalibrate from current position/velocity with the correct server duration.
          this.recalibrateFinalizeTargetDuration(adjustedTarget, d)
        } else {
          this.doFinalizeToTarget(
            adjustedTarget,
            cmd.payload.easingKey ?? EASING_KEY.SPIN_OUT,
            durationOk ? d : undefined
          )
        }
        break
      }
      case "Resolve":
        this.doResolve(cmd.payload.winnerId)
        break
      default:
        break
    }
  }

  private normalizeSectorPartyPercents(parties: SectorParty[]): SectorParty[] {
    return parties.map((p) => ({
      ...p,
      percent:
        typeof p.percent === "number" && Number.isFinite(p.percent)
          ? p.percent
          : Number(p.percent) || 0,
    }))
  }

  /**
   * Single entry: sync Pixi with Zustand when commands are drained (`detach`, navigation return).
   * Moving phases → full hydrate; static phases → visual paint without physics reset.
   */
  public reconcileWithRoomSlice(input: WheelRoomReconcileInput): void {
    if (!this.scene?.wheelContainer || !this.scene?.slicesContainer) return
    if (input.pendingCommandsCount > 0) return

    const { snapshot, userId } = input
    const syncKey = `${snapshot.roundId}:${snapshot.version}`
    const mapped = mapSnapshotToWheelParties(snapshot, userId)
    const status = snapshot.status

    const isMovingPhase =
      status === "SPINNING" || status === "TRICKS" || status === "SLOWING"
    // Moving phases are owned by Pixi commands — never reset physics on each WS tick.
    // Full hydrate only after detach left an empty wheel (navigation return).
    if (
      (isMovingPhase && this.parties.length === 0) ||
      (status === "RESOLVED" && this.parties.length === 0)
    ) {
      this.hydrateFromAckedRound(snapshot, { userId })
      this.lastVisualSyncKey = syncKey
      return
    }
    if (isMovingPhase) {
      return
    }

    // Weight tween keeps stale `this.parties` until the animation ends; reconciling now would
    // call `paintWheelVisualOnly` and clear `sectorTween` (instant snap instead of tween).
    if (this.sectorTween != null) {
      return
    }

    const layoutMatches =
      this.parties.length > 0 && this.parties.length === mapped.length
    if (this.lastVisualSyncKey === syncKey && layoutMatches) {
      this.syncSectorColorsOnly(mapped, userId, status)
      return
    }

    if (this.parties.length === 0 || this.parties.length !== mapped.length) {
      this.paintWheelVisualOnly(snapshot, mapped, userId)
      this.lastVisualSyncKey = syncKey
      return
    }

    this.syncSectorColorsOnly(mapped, userId, status)
    this.lastVisualSyncKey = syncKey
  }

  /**
   * After navigation away, `detach()` clears `parties` while Zustand already acked the Pixi queue.
   * Re-apply the last accepted snapshot so sectors match store without replaying deduped commands.
   */
  public hydrateFromAckedRound(
    snapshot: RoundSnapshot,
    opts?: { userId?: string }
  ): void {
    if (!this.scene?.wheelContainer || !this.scene?.slicesContainer) return

    const parties = mapSnapshotToWheelParties(snapshot, opts?.userId)
    const status = snapshot.status
    const visualDeg = this.snapshotVisualDegForRestore(snapshot, status)

    this.restoreWheelVisualFromSnapshot(
      parties,
      status,
      visualDeg,
      opts?.userId
    )
    this.lastDedupBackendRoundId = snapshot.roundId
    this.lastRoundVersion = snapshot.version

    const timing = getEffectiveTiming(snapshot)
    const nowWall = this.clock.wallNow()
    if (status === "SPINNING" && timing.spinningEndsAt != null) {
      const targetDeg = centidegreesToDegrees(
        getEffectiveTargetCoordinate(snapshot)
      )
      this.doStartSpin(targetDeg, EASING_KEY.LINEAR, timing.spinningEndsAt)
    }

    if (status === "TRICKS") {
      const tricksEndMs = resolveTricksEndsAtMs(snapshot)
      const targetDeg = centidegreesToDegrees(
        getEffectiveTargetCoordinate(snapshot)
      )
      const tricksDurationMs =
        tricksEndMs != null ? Math.max(0, tricksEndMs - nowWall) : undefined
      this.doEnterTricksPhase(
        tricksEndMs ?? timing.tricksEndsAt ?? undefined,
        targetDeg,
        tricksDurationMs
      )
    } else if (status === "SLOWING") {
      const targetDeg = centidegreesToDegrees(
        getEffectiveTargetCoordinate(snapshot)
      )
      const durationMs = computeSlowingFinalizeDurationMs(nowWall, snapshot)
      this.doFinalizeToTarget(
        targetDeg,
        EASING_KEY.SPIN_OUT,
        durationMs
      )
    }

    if (status === "RESOLVED") {
      this.doResolve(snapshot.winnerPartyId ?? undefined)
    } else if (
      status === "SPINNING" ||
      status === "TRICKS" ||
      status === "SLOWING"
    ) {
      this.scheduleWheelTick()
    }

    this.tryRenderApp()
  }

  /**
   * Server snapshot no longer includes the current user's trick after an optimistic apply.
   * Resyncs TRICKS wall-clock and drops one-time push/freeze overlays — without `doInitWheelScene`.
   */
  public resyncAfterOwnTrickRemoved(
    snapshot: RoundSnapshot,
    userId: string
  ): void {
    if (snapshot.status !== "TRICKS") return
    if (!this.scene?.wheelContainer || !this.scene?.slicesContainer) return

    const inTricksCurve =
      this.physics.isSpinningPhase &&
      this.physics.tricksPhaseStartTime > 0 &&
      !this.physics.isFinalizing
    if (!inTricksCurve) return

    const roomId = snapshot.roundId
    this.revertTrickVisualIfApplied(roomId, userId, TRICK_TYPE_PUSH_WHEEL)
    this.revertTrickVisualIfApplied(roomId, userId, TRICK_TYPE_FREEZE_WHEEL)

    const tricksEndMs = resolveTricksEndsAtMs(snapshot)
    if (tricksEndMs != null) {
      this.doApplyTrickPush(tricksEndMs, false)
    } else {
      this.tick()
    }
  }

  private revertTrickVisualIfApplied(
    roomId: string,
    userId: string,
    trickType: string
  ): void {
    const key = `${roomId}:${userId}:${trickType}`
    if (!this.trickVisualEffectKeys.delete(key)) return

    if (trickType === TRICK_TYPE_FREEZE_WHEEL) {
      this.physics.trickSpeedDampen = null
      this.trixChar?.endFreeze()
    } else if (trickType === TRICK_TYPE_PUSH_WHEEL) {
      this.physics.trickSpeedBoost = null
    }
  }

  /** Used to avoid double-hydration when pending Pixi commands already painted sectors this mount. */
  public getSectorPartyCount(): number {
    return this.parties.length
  }

  private snapshotVisualDegForRestore(
    snapshot: RoundSnapshot,
    status?: string
  ): number | undefined {
    if (snapshot.currentCoordinate != null) {
      return centidegreesToDegrees(snapshot.currentCoordinate)
    }
    if (
      status === "SPINNING" ||
      status === "TRICKS" ||
      status === "SLOWING" ||
      status === "RESOLVED"
    ) {
      return centidegreesToDegrees(getEffectiveTargetCoordinate(snapshot))
    }
    return undefined
  }

  /**
   * Repaint sectors/avatars/mascot after navigation without `doInitWheelScene`
   * (preserves Pixi command dedup and avoids mascot/physics hard reset).
   */
  private restoreWheelVisualFromSnapshot(
    parties: SectorParty[],
    status?: string,
    visualDeg?: number,
    userId?: string
  ): void {
    if (!this.scene) return

    const previousParties = this.parties.map((p) => ({ ...p }))
    parties = this.normalizeSectorPartyPercents(parties)
    this.sectorTween = null
    if (visualDeg != null && Number.isFinite(visualDeg)) {
      this.physics.currentRotationDeg = visualDeg
      this.physics.trickPermanentOffsetDeg = 0
      this.scene.resetDisplay(visualDeg)
    }
    this.physics.allowIdleSpin = status === "SPINNING" || status === "TRICKS"
    this.lifecyclePhase = this.mapStatusToPhase(status)
    this.syncMascotForStatus(status)
    this.parties = parties
    const paletteRevertTween = this.startBettingPaletteColorTweenIfNeeded(
      previousParties,
      parties,
      status
    )
    if (!paletteRevertTween) {
      this.scene.drawSectors(parties)
    }
    this.scene.drawCenter()
    this.scene.drawPointer()
    this.scene.buildAvatarItems(parties)
    this.scene.setCurrentUserId(userId ?? this.currentUserId ?? null, parties)
    if (parties.length > 0) {
      this.physics.lastTickTime = this.clock.perfNow()
    }
  }

  /** Repaint sectors/avatars/mascot without resetting physics or command dedup. */
  private paintWheelVisualOnly(
    snapshot: RoundSnapshot,
    parties: SectorParty[],
    userId?: string
  ): void {
    if (!this.scene) return

    const status = snapshot.status
    const visualDeg = this.snapshotVisualDegForRestore(snapshot, status)
    const previousParties = this.parties.map((p) => ({ ...p }))
    this.parties = this.normalizeSectorPartyPercents(parties)
    this.sectorTween = null
    if (visualDeg != null && Number.isFinite(visualDeg)) {
      this.physics.currentRotationDeg = visualDeg
      this.physics.trickPermanentOffsetDeg = 0
      this.scene.resetDisplay(visualDeg)
    }
    this.physics.allowIdleSpin = false
    if (
      status === "WAITING" ||
      status === "BETTING" ||
      status === "RESOLVED"
    ) {
      this.lifecyclePhase = status
    }

    this.syncMascotForStatus(status)

    const paletteRevertTween = this.startBettingPaletteColorTweenIfNeeded(
      previousParties,
      this.parties,
      status
    )
    if (!paletteRevertTween) {
      this.scene.drawSectors(this.parties)
    }
    this.scene.drawCenter()
    this.scene.drawPointer()
    this.scene.buildAvatarItems(this.parties)
    this.scene.setCurrentUserId(userId ?? this.currentUserId ?? null, this.parties)

    this.lastDedupBackendRoundId = snapshot.roundId
    this.lastRoundVersion = snapshot.version

    if (status === "RESOLVED") {
      this.doResolve(snapshot.winnerPartyId ?? undefined)
    }

    this.tryRenderApp()
  }

  private syncMascotForStatus(status?: string): void {
    if (
      status === "SPINNING" ||
      status === "TRICKS" ||
      status === "SLOWING" ||
      status === "RESOLVED"
    ) {
      this.trixChar?.playBigIdle()
    } else {
      this.trixChar?.playMiniIdle()
    }
  }

  private syncSectorColorsOnly(
    mapped: SectorParty[],
    userId: string | undefined,
    status: string
  ): void {
    if (
      status === "SPINNING" ||
      status === "TRICKS" ||
      status === "SLOWING"
    ) {
      return
    }
    if (this.parties.length === 0 || mapped.length !== this.parties.length) {
      return
    }
    if (this.sectorTweenBlocksIdleSpin()) {
      return
    }

    if (wheelPartiesColorsEqual(this.parties, mapped)) {
      this.scene.setCurrentUserId(this.currentUserId ?? userId ?? null, this.parties)
      return
    }

    this.parties = this.parties.map((p, i) => ({
      ...p,
      color: mapped[i]?.color ?? p.color,
    }))

    if (this.sectorTween) {
      this.sectorTween = { ...this.sectorTween, parties: this.parties }
    } else {
      this.scene?.drawSectors(this.parties)
    }
    this.scene?.setCurrentUserId(this.currentUserId ?? userId ?? null, this.parties)
  }

  private doInitWheelScene(
    parties: SectorParty[],
    status?: string,
    initialVisualAngleDeg?: number
  ): void {
    const previousParties = this.parties.map((p) => ({ ...p }))
    parties = this.normalizeSectorPartyPercents(parties)
    this.physics.currentRotationDeg = 0
    this.physics.trickPermanentOffsetDeg = 0
    this.physics.trickSpeedBoost = null
    this.physics.trickSpeedDampen = null
    this.physics.finalizeState = null
    this.physics.isSpinningPhase = false
    this.physics.spinAngularVelDegPerMs = 0
    this.physics.spinStartedAt = 0
    this.physics.spinDurationMs = 0
    this.physics.tricksPhaseStartTime = 0
    this.physics.tricksPhaseStartTimeWallMs = 0
    this.physics.tricksDurationMs = 0
    this.physics.tricksEntryVelDegPerMs = 0
    this.physics.tricksEndFromFallback = false
    this.physics.scheduledTricksAtMs = null
    this.physics.scheduledFinalizeAtMs = null
    this.physics.scheduledFinalizeTargetDeg = null
    this.physics.scheduledFinalizeDurationMs = null
    this.physics.isFinalizing = false
    this.sectorTween = null
    this.physics.roundResolved = false
    this.prevRotationDeg = 0
    this.appliedCommandIds.clear()
    this.trickVisualEffectKeys.clear()
    this.lastRoundVersion = 0
    if (
      initialVisualAngleDeg != null &&
      Number.isFinite(initialVisualAngleDeg)
    ) {
      this.physics.currentRotationDeg = initialVisualAngleDeg
    }
    const initAngle =
      initialVisualAngleDeg != null && Number.isFinite(initialVisualAngleDeg)
        ? initialVisualAngleDeg
        : 0
    this.scene?.resetDisplay(initAngle)
    this.physics.allowIdleSpin = status === "SPINNING" || status === "TRICKS"
    this.lifecyclePhase = this.mapStatusToPhase(status)
    this.emitPhase(`InitWheelScene status=${status ?? "?"}`)
    this.syncMascotForStatus(status)
    this.parties = parties
    const paletteRevertTween = this.startBettingPaletteColorTweenIfNeeded(
      previousParties,
      parties,
      status
    )
    if (!paletteRevertTween) {
      this.scene?.drawSectors(parties)
    }
    this.scene?.drawCenter()
    this.scene?.drawPointer()
    if (parties.length > 0) {
      this.physics.lastTickTime = this.clock.perfNow()
      this.scheduleWheelTick()
    }
  }

  private doUpdateSectorWeightsTween(
    parties: SectorParty[],
    durationMs: number,
    easingKey: string,
    status?: string
  ): void {
    parties = this.normalizeSectorPartyPercents(parties)
    this.physics.allowIdleSpin = status === "SPINNING" || status === "TRICKS"
    if (status === "WAITING" || status === "BETTING" || status === "RESOLVED") {
      this.lifecyclePhase = status
    }
    this.emitPhase(`UpdateSectorWeights status=${status ?? "?"}`)
    this.trixChar?.playMiniAction()
    if (parties.length === 0) {
      this.doInitWheelScene(parties, status, undefined)
      return
    }
    // First sector (0→1): animate from tiny slice to full circle
    if (this.parties.length === 0 && parties.length === 1) {
      const fromAngles = [0, 1]
      const toAngles = [0, 360]
      const lerpPadLen = Math.max(
        parties.length + 1,
        fromAngles.length,
        toAngles.length
      )
      this.emitSectorWeightsTweenDiag({
        durationMs,
        easingKey,
        fromPartyCount: this.parties.length,
        toPartyCount: parties.length,
        fromAnglesDeg: fromAngles,
        toAnglesDeg: toAngles,
        normalizedFromDeg: fromAngles,
        normalizedToDeg: toAngles,
        lerpPadLen,
        toParties: parties,
      })
      const now = this.clock.perfNow()
      this.physics.lastTickTime = now
      this.sectorTween = {
        fromAngles,
        toAngles,
        parties,
        durationMs,
        easing: getEasing(easingKey),
        startTime: now,
      }
      this.tick()
      return
    }
    const fromAnglesRaw = computeSectorStartAngles(this.parties)
    const toAnglesRaw = computeSectorStartAngles(parties)
    const normalized = this.normalizeSectorAnglesForTween(
      fromAnglesRaw,
      this.parties,
      toAnglesRaw,
      parties
    )
    const lerpPadLen = Math.max(
      parties.length + 1,
      normalized.from.length,
      normalized.to.length
    )
    this.emitSectorWeightsTweenDiag({
      durationMs,
      easingKey,
      fromPartyCount: this.parties.length,
      toPartyCount: parties.length,
      fromAnglesDeg: fromAnglesRaw,
      toAnglesDeg: toAnglesRaw,
      normalizedFromDeg: normalized.from,
      normalizedToDeg: normalized.to,
      lerpPadLen,
      toParties: parties,
    })
    const now = this.clock.perfNow()
    this.physics.lastTickTime = now
    // Same layout as already painted (e.g. RESOLVED echo of current weights) — skip RAF tween noise.
    if (this.areSectorAngleLayoutsEqual(normalized.from, normalized.to)) {
      this.parties = parties
      this.scene?.buildAvatarItems(parties)
      this.scene?.drawSectors(parties)
      return
    }
    // Rebuild avatar items immediately for new parties so they appear from tween start.
    this.scene?.buildAvatarItems(parties)
    this.sectorTween = {
      fromAngles: normalized.from,
      toAngles: normalized.to,
      parties,
      durationMs,
      easing: getEasing(easingKey),
      startTime: now,
    }
    this.tick()
  }

  /** True if boundary arrays match within ε° (no visible sector tween needed). */
  private areSectorAngleLayoutsEqual(
    a: number[],
    b: number[],
    epsDeg = 0.02
  ): boolean {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      if (Math.abs((a[i] ?? 0) - (b[i] ?? 0)) > epsDeg) return false
    }
    return true
  }

  /**
   * Lerp needs equal-length boundary arrays. If a 1-sector full wheel [0,360] is paired with
   * N>1 sectors, `fromAngles.map` would truncate to 2 points → one 360° segment and
   * `drawSectorsFromAngles` would paint only parties[0]. Split the full circle at the start.
   */
  private padSectorAnglesForLerp(
    angles: number[],
    targetLen: number,
    parties: SectorParty[]
  ): number[] {
    if (angles.length >= targetLen) return [...angles]
    const a0 = angles[0] ?? 0
    const a1 = angles[angles.length - 1] ?? 360
    const span = a1 - a0
    if (
      angles.length === 2 &&
      parties.length >= 2 &&
      targetLen >= 3 &&
      Math.abs(span - 360) < 0.1
    ) {
      const split = [a0, a0, a1]
      if (targetLen > split.length) {
        const out = [...split]
        while (out.length < targetLen) out.push(out[out.length - 1]!)
        return out
      }
      return split.slice(0, targetLen)
    }
    const out = [...angles]
    while (out.length < targetLen) out.push(out[out.length - 1]!)
    return out
  }

  private normalizeSectorAnglesForTween(
    fromAngles: number[],
    fromParties: SectorParty[],
    toAngles: number[],
    toParties: SectorParty[]
  ): { from: number[]; to: number[] } {
    const targetSegmentCount = Math.max(fromParties.length, toParties.length)
    if (targetSegmentCount <= 0) return { from: [0], to: [0] }

    const build = (angles: number[], parties: SectorParty[]): number[] => {
      if (parties.length === 0) {
        const out = new Array(targetSegmentCount + 1).fill(0)
        out[out.length - 1] = 360
        return out
      }
      if (parties.length === targetSegmentCount) return angles
      if (parties.length === 1 && targetSegmentCount > 1) {
        const out = [0]
        for (let i = 1; i < targetSegmentCount; i++) out.push(0)
        out.push(360)
        return out
      }
      const out = [0]
      for (let i = 1; i <= targetSegmentCount; i++) {
        out.push(angles[Math.min(i, angles.length - 1)] ?? 360)
      }
      return out
    }

    return {
      from: build(fromAngles, fromParties),
      to: build(toAngles, toParties),
    }
  }

  /**
   * SPINNING: velocity-driven (no position tween).
   * Ramp-up to peak over SPINNING_RAMP_UP_MS, then linear decay to TRICKS_START.
   */
  private doStartSpin(
    _targetAngleDeg: number,
    _easingKey: string,
    spinningEndsAt?: number,
    durationMsOverride?: number
  ): void {
    if (
      this.trySyncPhase(
        "SPINNING",
        this.physics.isSpinningPhase && this.physics.tricksPhaseStartTime === 0,
        "StartSpinSync",
        () => {
          const spinEndMs =
            spinningEndsAt != null && Number.isFinite(spinningEndsAt)
              ? normalizeUnixTimestampToMs(spinningEndsAt)
              : null
          if (
            durationMsOverride != null &&
            Number.isFinite(durationMsOverride) &&
            durationMsOverride > 0
          ) {
            this.physics.spinDurationMs = Math.max(
              this.minSpinDurationMs,
              durationMsOverride
            )
            if (spinEndMs != null) {
              this.physics.scheduledTricksAtMs =
                this.clock.wallNow() + durationMsOverride
            }
          } else if (spinEndMs != null) {
            this.physics.spinDurationMs = Math.max(
              this.minSpinDurationMs,
              spinEndMs - this.clock.wallNow()
            )
            this.physics.scheduledTricksAtMs = spinEndMs
          }
        }
      )
    )
      return
    const now = this.clock.perfNow()
    const spinEndMs =
      spinningEndsAt != null && Number.isFinite(spinningEndsAt)
        ? normalizeUnixTimestampToMs(spinningEndsAt)
        : null

    this.physics.spinDurationMs =
      durationMsOverride != null &&
      Number.isFinite(durationMsOverride) &&
      durationMsOverride > 0
        ? Math.max(
            getPvpRouletteRuntime().SPINNING_RAMP_UP_MS + 100,
            durationMsOverride
          )
        : spinEndMs != null
          ? // When server provides spinningEndsAt, respect that rhythm so TRICKS starts
            // near TRICKS_START_REV_PER_SEC instead of being stretched by emulator min duration.
            Math.max(
              getPvpRouletteRuntime().SPINNING_RAMP_UP_MS + 100,
              spinEndMs - this.clock.wallNow()
            )
          : this.minSpinDurationMs

    this.physics.spinStartedAt = now
    this.physics.finalizeState = null
    this.physics.allowIdleSpin = true
    this.physics.isSpinningPhase = true
    this.physics.isFinalizing = false
    this.physics.tricksPhaseStartTime = 0
    this.physics.tricksDurationMs = 0
    this.physics.tricksEntryVelDegPerMs = 0
    this.physics.tricksEndFromFallback = false
    this.physics.spinAngularVelDegPerMs = 0

    this.physics.scheduledTricksAtMs =
      spinEndMs != null
        ? durationMsOverride != null &&
          Number.isFinite(durationMsOverride) &&
          durationMsOverride > 0
          ? this.clock.wallNow() + durationMsOverride
          : spinEndMs
        : null
    this.physics.scheduledFinalizeAtMs = null
    this.physics.scheduledFinalizeTargetDeg = null
    this.lifecyclePhase = "SPINNING"
    this.trixChar?.playBigIdle()
    this.emitPhase(
      `StartSpin peak=${(getPvpRouletteRuntime().SPINNING_PEAK_REV_PER_SEC * 360).toFixed(0)}°/s`
    )
    // Иначе первый кадр SPINNING после долгого BETTING берёт dt ~ секунды при u≈0 → «мёртвый» разгон.
    this.physics.lastTickTime = now
    this.startTricksSectorColorTweenIfNeeded()
    this.tick()
  }

  /** TRICKS phase: smooth p^TRICKS_DECAY_EXPONENT speed decay from TRICKS_START to TRICKS_END. */
  private doEnterTricksPhase(
    tricksEndsAt?: number,
    targetAngleDeg?: number,
    durationMsOverride?: number
  ): void {
    const nowWall = this.clock.wallNow()
    const alreadyInTricks =
      this.physics.isSpinningPhase &&
      this.physics.tricksPhaseStartTime > 0 &&
      !this.physics.isFinalizing
    if (!alreadyInTricks) {
      this.physics.scheduledFinalizeDurationMs = null
    }
    const nowPerf = this.clock.perfNow()
    const currentVelBeforeDurationSync = alreadyInTricks
      ? this.physics.computeSpinningVelocity(nowPerf, this.clock.wallNow())
      : 0
    if (
      durationMsOverride != null &&
      Number.isFinite(durationMsOverride) &&
      durationMsOverride > 0
    ) {
      const effectiveEndMs = nowWall + durationMsOverride
      this.physics.scheduledFinalizeAtMs = effectiveEndMs
      if (alreadyInTricks) {
        const durationFromStart = Math.max(
          nowWall - this.physics.tricksPhaseStartTimeWallMs,
          effectiveEndMs - this.physics.tricksPhaseStartTimeWallMs
        )
        this.physics.updateTricksDurationKeepingVelocity(
          durationFromStart,
          nowWall,
          currentVelBeforeDurationSync
        )
      } else {
        this.physics.tricksDurationMs = Math.max(0, durationMsOverride)
      }
      this.physics.tricksEndFromFallback = false
    } else if (tricksEndsAt != null && Number.isFinite(tricksEndsAt)) {
      const endMs =
        tricksEndsAt > 0 && tricksEndsAt < 1e12
          ? Math.round(tricksEndsAt * 1000)
          : tricksEndsAt
      const currentPlannedEndMs =
        this.physics.tricksPhaseStartTimeWallMs > 0 &&
        this.physics.tricksDurationMs > 0
          ? this.physics.tricksPhaseStartTimeWallMs +
            this.physics.tricksDurationMs
          : endMs
      // If TRICKS currently follows fallback timeline, authoritative server end must replace it
      // (including shortening). Once authoritative end is known, subsequent updates can only extend.
      const effectiveEndMs = alreadyInTricks
        ? this.physics.tricksEndFromFallback
          ? endMs
          : Math.max(currentPlannedEndMs, endMs)
        : endMs
      this.physics.scheduledFinalizeAtMs = effectiveEndMs
      if (alreadyInTricks) {
        // Keep existing curve origin; only rescale to the newly confirmed end.
        const durationFromStart = Math.max(
          nowWall - this.physics.tricksPhaseStartTimeWallMs,
          effectiveEndMs - this.physics.tricksPhaseStartTimeWallMs
        )
        this.physics.updateTricksDurationKeepingVelocity(
          durationFromStart,
          nowWall,
          currentVelBeforeDurationSync
        )
      } else {
        this.physics.tricksDurationMs = Math.max(0, effectiveEndMs - nowWall)
      }
      this.physics.tricksEndFromFallback = false
    } else {
      const fallbackEnd =
        nowWall + getPvpRouletteRuntime().TRICKS_CLIENT_SCHEDULE_FALLBACK_MS
      this.physics.scheduledFinalizeAtMs = fallbackEnd
      if (alreadyInTricks) {
        const durationFromStart = Math.max(
          nowWall - this.physics.tricksPhaseStartTimeWallMs,
          fallbackEnd - this.physics.tricksPhaseStartTimeWallMs
        )
        this.physics.updateTricksDurationKeepingVelocity(
          durationFromStart,
          nowWall,
          currentVelBeforeDurationSync
        )
      } else {
        this.physics.tricksDurationMs = Math.max(0, fallbackEnd - nowWall)
      }
      this.physics.tricksEndFromFallback = true
    }
    if (targetAngleDeg != null && Number.isFinite(targetAngleDeg)) {
      this.physics.scheduledFinalizeTargetDeg = targetAngleDeg
    }
    this.physics.scheduledTricksAtMs = null

    // Avoid re-entering TRICKS curve (which causes a velocity hump) when the
    // scheduler already entered TRICKS and the authoritative command arrives later.
    if (this.trySyncPhase("TRICKS", alreadyInTricks, "EnterTricksPhaseSync"))
      return

    const now = this.clock.perfNow()
    let entryVel = this.physics.computeSpinningVelocity(now, nowWall)
    if (Math.abs(entryVel) < 1e-6) {
      const rt = getPvpRouletteRuntime()
      entryVel =
        (rt.TRICKS_START_REV_PER_SEC * 360 * rt.WHEEL_VISUAL_SPIN_SIGN) / 1000
    }
    this.physics.tricksEntryVelDegPerMs = entryVel
    this.physics.spinStartedAt = 0
    this.physics.isSpinningPhase = true
    this.physics.isFinalizing = false
    this.physics.tricksPhaseStartTime = now
    this.physics.tricksPhaseStartTimeWallMs = nowWall
    this.physics.spinAngularVelDegPerMs = entryVel
    this.physics.lastTickTime = now
    this.lifecyclePhase = "TRICKS"
    this.emitPhase("EnterTricksPhase")
    // Manual emulator can jump BETTING → TRICKS skipping SPINNING; ensure big family
    // so playTrickReaction (big_trick) is not a no-op.
    this.trixChar?.playBigIdle()
    this.startTricksSectorColorTweenIfNeeded()
  }

  /**
   * Apply trick push as a speed boost (Python-style).
   * Peak speed of TRICK_BOOST_PEAK_DEG_PER_SEC decaying linearly over TRICK_BOOST_DURATION_MS.
   * Stacking: max(remaining, full duration) so rapid pushes match pygame prototype.
   */
  /**
   * @param applyImpulse When false, only sync TRICKS window from `tricksEndsAt` (server echo after optimistic).
   */
  private doApplyTrickPush(tricksEndsAt?: number, applyImpulse = true): void {
    if (tricksEndsAt != null && Number.isFinite(tricksEndsAt)) {
      const nowWall = this.clock.wallNow()
      const endMs =
        tricksEndsAt > 0 && tricksEndsAt < 1e12
          ? Math.round(tricksEndsAt * 1000)
          : tricksEndsAt
      const plannedEndMs =
        this.physics.tricksPhaseStartTimeWallMs > 0 &&
        this.physics.tricksDurationMs > 0
          ? this.physics.tricksPhaseStartTimeWallMs +
            this.physics.tricksDurationMs
          : endMs
      const effectiveEndMs = this.physics.tricksEndFromFallback
        ? endMs
        : Math.max(plannedEndMs, endMs)
      this.physics.scheduledFinalizeAtMs = effectiveEndMs
      // Rescale decay curve to effective end from the existing TRICKS origin.
      const durationFromStart = Math.max(
        nowWall - this.physics.tricksPhaseStartTimeWallMs,
        effectiveEndMs - this.physics.tricksPhaseStartTimeWallMs
      )
      const currentVelBeforeDurationSync = this.physics.computeSpinningVelocity(
        this.clock.perfNow(),
        this.clock.wallNow()
      )
      this.physics.updateTricksDurationKeepingVelocity(
        durationFromStart,
        nowWall,
        currentVelBeforeDurationSync
      )
      this.physics.tricksEndFromFallback = false
    }
    if (!applyImpulse) {
      this.emitPhase("ApplyTrickPushSyncOnly")
      this.tick()
      return
    }
    this.emitPhase("ApplyTrickPush")
    const nextRemaining = Math.max(
      this.physics.trickSpeedBoost?.remainingMs ?? 0,
      getPvpRouletteRuntime().TRICK_BOOST_DURATION_MS
    )
    this.physics.trickSpeedBoost = {
      remainingMs: nextRemaining,
      durationMs: getPvpRouletteRuntime().TRICK_BOOST_DURATION_MS,
      peakDegPerMs: getPvpRouletteRuntime().TRICK_BOOST_PEAK_DEG_PER_SEC / 1000,
    }
    this.trixChar?.playPush()
    this.trixChar?.playTrickReaction()
    this.tick()
  }

  /** FREEZE_WHEEL: for TRICK_FREEZE_DURATION_MS, velocity → factor × instantaneous. */
  private doApplyTrickFreeze(tricksEndsAt?: number, applyImpulse = true): void {
    if (tricksEndsAt != null && Number.isFinite(tricksEndsAt)) {
      const nowWall = this.clock.wallNow()
      const endMs =
        tricksEndsAt > 0 && tricksEndsAt < 1e12
          ? Math.round(tricksEndsAt * 1000)
          : tricksEndsAt
      const plannedEndMs =
        this.physics.tricksPhaseStartTimeWallMs > 0 &&
        this.physics.tricksDurationMs > 0
          ? this.physics.tricksPhaseStartTimeWallMs +
            this.physics.tricksDurationMs
          : endMs
      const effectiveEndMs = this.physics.tricksEndFromFallback
        ? endMs
        : Math.max(plannedEndMs, endMs)
      this.physics.scheduledFinalizeAtMs = effectiveEndMs
      // Rescale decay curve to effective end from the existing TRICKS origin.
      const durationFromStart = Math.max(
        nowWall - this.physics.tricksPhaseStartTimeWallMs,
        effectiveEndMs - this.physics.tricksPhaseStartTimeWallMs
      )
      const currentVelBeforeDurationSync = this.physics.computeSpinningVelocity(
        this.clock.perfNow(),
        this.clock.wallNow()
      )
      this.physics.updateTricksDurationKeepingVelocity(
        durationFromStart,
        nowWall,
        currentVelBeforeDurationSync
      )
      this.physics.tricksEndFromFallback = false
    }
    if (!applyImpulse) {
      this.emitPhase("ApplyTrickFreezeSyncOnly")
      this.tick()
      return
    }
    this.emitPhase("ApplyTrickFreeze")
    // FREEZE stacks by extending total freeze time window.
    const nextRemaining =
      (this.physics.trickSpeedDampen?.remainingMs ?? 0) +
      getPvpRouletteRuntime().TRICK_FREEZE_DURATION_MS
    this.physics.trickSpeedDampen = {
      remainingMs: nextRemaining,
      durationMs: getPvpRouletteRuntime().TRICK_FREEZE_DURATION_MS,
    }
    // Spine freeze_loop is endless — match physics overlay lifetime (stacks refresh total ms).
    this.trixChar?.playFreezeStart(nextRemaining)
    this.trixChar?.playTrickReaction()
    this.tick()
  }

  private doSyncPlannedFinalizeTarget(targetAngleDeg: number): void {
    if (!Number.isFinite(targetAngleDeg)) return
    if (this.physics.isFinalizing) return
    this.physics.scheduledFinalizeTargetDeg = targetAngleDeg
    this.emitPhase("SyncPlannedFinalizeTarget")
  }

  /**
   * Retunes TRICKS decay after the wall window (phase start → tricks_ends_at) changes so base
   * angular speed stays continuous when `tricks_ends_at` moves. Idempotent for redundant calls:
   * if the window length barely changed, skip — `currentVelDegPerMs` comes from
   * `computeSpinningVelocity` (base curve only); re-solving with the same span after a PUSH
   * overlay nudges `tricksEntryVelDegPerMs` and reads as a short "stuck" frame.
   */
  /**
   * SLOWING: crossfade-power-curve deceleration from current speed to 0.
   *
   *   position(s) = startDeg + delta · f(s)
   *   f(s) = 1 − (1−w(s))·(1−s)^expEntry − w(s)·(1−s)^expTail
   *   w(s) = smootherstep(s) = 6s⁵ − 15s⁴ + 10s³
   *
   * `expEntry` is **always** the natural `v0·T/|delta|` (clamped only to ≥1.0 for stability),
   * so `v(0) = v0` exactly — нет «толчка» скорости при TRICKS→SLOWING. `expTail ≥ SLOWING_TARGET_EXP`
   * obeспечивает плавный «хвост» торможения. При `expEntry ≥ expTail` cross-fade схлопывается
   * до стандартной `1 − (1−s)^exp` без оверхеда (без потери скорости).
   *
   * Full extra 360° turns are chosen only to keep `expEntry` close to `SLOWING_TARGET_EXP`
   * for the fixed server `durationMs` — not a separate "min extra spin" product rule.
   */
  private doFinalizeToTarget(
    targetAngleDeg: number,
    _easingKey: string,
    durationMsOverride?: number
  ): void {
    if (
      this.trySyncPhase(
        "SLOWING",
        this.physics.isFinalizing,
        "FinalizeToTargetSync",
        () => {
          if (
            durationMsOverride != null &&
            Number.isFinite(durationMsOverride) &&
            durationMsOverride > 0
          ) {
            this.recalibrateFinalizeTargetDuration(
              targetAngleDeg,
              durationMsOverride
            )
          }
        }
      )
    )
      return
    if (this.physics.isFinalizing) return
    this.physics.isFinalizing = true
    const now = this.clock.perfNow()
    const velDegPerMs = this.physics.getVisualAngularVelDegPerMs(
      now,
      this.clock.wallNow()
    )
    this.physics.bakeTrickOverlayIntoBaseRotation()
    this.prevRotationDeg = this.physics.currentRotationDeg

    const durationMs =
      this.physics.resolveFinalizeDurationMs(durationMsOverride)

    const {
      deltaDeg: delta,
      expEntry,
      expTail,
    } = solveFinalizeDelta({
      velDegPerMs,
      durationMs,
      targetAngleDeg,
      currentAngleDeg: this.physics.currentRotationDeg,
      spinSign: getPvpRouletteRuntime().WHEEL_VISUAL_SPIN_SIGN,
      targetExp: getPvpRouletteRuntime().SLOWING_TARGET_EXP,
    })

    this.physics.allowIdleSpin = false
    this.physics.scheduledFinalizeAtMs = null
    this.physics.finalizeState = {
      startedAt: now,
      durationMs,
      startDeg: this.physics.currentRotationDeg,
      deltaDeg: delta,
      targetDeg: this.physics.currentRotationDeg + delta,
      expEntry,
      expTail,
    }
    this.physics.isSpinningPhase = false
    this.physics.spinAngularVelDegPerMs = 0
    this.physics.spinStartedAt = 0
    this.physics.tricksPhaseStartTime = 0
    this.lifecyclePhase = "SLOWING"
    this.emitPhase(
      `FinalizeToTarget expEntry=${expEntry.toFixed(3)} expTail=${expTail.toFixed(3)} v0=${(velDegPerMs * 1000).toFixed(1)}°/s`
    )
    this.trixChar?.endFreeze()
    this.tick()
  }

  /**
   * Called when FinalizeToTarget command arrives with a server-provided durationMs while
   * the tick-scheduler has already started finalization with the fallback duration (~16ms ago).
   * Re-initializes finalizeState from current position/velocity with the correct duration.
   * Since the elapsed time is tiny, the visual transition is seamless.
   */
  private recalibrateFinalizeTargetDuration(
    targetAngleDeg: number,
    newDurationMs: number
  ): void {
    if (!this.physics.finalizeState) return
    const now = this.clock.perfNow()
    const signedVel = computeSlowingVelocity(this.physics.finalizeState, now)
    // Reset finalize state and temporarily expose velocity through spinAngularVelDegPerMs
    // so that getVisualAngularVelDegPerMs picks it up inside doFinalizeToTarget.
    this.physics.isFinalizing = false
    this.physics.finalizeState = null
    this.physics.isSpinningPhase = true
    this.physics.spinAngularVelDegPerMs = signedVel
    this.doFinalizeToTarget(
      targetAngleDeg,
      EASING_KEY.SPIN_OUT,
      Math.max(1, newDurationMs)
    )
  }

  /**
   * Resolve: mark round as finished.
   * Stops idle spin — wheel stays on winning position until new round (InitWheelScene).
   * FinalizeToTarget runs to completion first, then wheel is frozen.
   */
  private doResolve(winnerId?: string): void {
    if (
      this.trySyncPhase("RESOLVED", this.physics.roundResolved, "ResolveSync")
    )
      return
    this.physics.roundResolved = true
    this.physics.isSpinningPhase = false
    this.physics.isFinalizing = false
    this.lifecyclePhase = "RESOLVED"
    this.emitPhase("Resolve")
    if (winnerId) {
      // TODO: highlight winning sector
    }
    this.trixChar?.endFreeze()
    this.tick()
  }

  private mapStatusToPhase(status?: string): EngineLifecyclePhase {
    switch (status) {
      case "WAITING":
      case "BETTING":
      case "SPINNING":
      case "TRICKS":
      case "SLOWING":
      case "RESOLVED":
        return status
      default:
        return "IDLE"
    }
  }

  /**
   * Common idempotent guard for late duplicate phase commands.
   * When phase is already active and condition confirms re-entry, applies optional sync
   * side-effects, emits Sync diagnostic, and short-circuits caller.
   */
  private trySyncPhase(
    phase: EngineLifecyclePhase,
    canSync: boolean,
    syncLabel: string,
    onSync?: () => void
  ): boolean {
    if (this.lifecyclePhase !== phase || !canSync) return false
    onSync?.()
    this.lifecyclePhase = phase
    this.emitPhase(syncLabel)
    return true
  }

  private tick = (): void => {
    if (this.tornDown || this.isTicking) return
    this.isTicking = true
    try {
      const now = this.clock.perfNow()
      const nowMs = this.clock.wallNow()
      this.tickAdvanceScheduledPhases(nowMs)
      this.tickSectorTween(now)
      const {
        rotationDeg: angle,
        trickOffset,
        physicsNeedsTick,
      } = this.physics.advance(
        now,
        nowMs,
        this.parties.length,
        this.sectorTweenBlocksIdleSpin()
      )
      this.tickRender(now, nowMs, angle, trickOffset, physicsNeedsTick)
    } finally {
      this.isTicking = false
    }
  }

  private tickAdvanceScheduledPhases(nowMs: number): void {
    if (
      this.physics.scheduledTricksAtMs != null &&
      !this.physics.finalizeState &&
      this.physics.isSpinningPhase &&
      this.physics.tricksPhaseStartTime === 0 &&
      nowMs >= this.physics.scheduledTricksAtMs
    ) {
      this.doEnterTricksPhase()
    }

    if (
      this.physics.scheduledFinalizeAtMs != null &&
      this.physics.isSpinningPhase &&
      this.physics.tricksPhaseStartTime > 0 &&
      !this.physics.isFinalizing &&
      this.physics.scheduledFinalizeTargetDeg != null &&
      nowMs >= this.physics.scheduledFinalizeAtMs &&
      this.physics.scheduledFinalizeDurationMs != null &&
      this.physics.scheduledFinalizeDurationMs > 0
    ) {
      // Without server slowing duration, stay on the TRICKS curve (t clamped → TRICKS_END speed)
      // until `FinalizeToTarget` arrives with `durationMs` + authoritative target — avoids a 1ms
      // ease-out and a visually dead wheel when WS lags optimistic TRICKS→SLOWING.
      this.doFinalizeToTarget(
        this.physics.scheduledFinalizeTargetDeg,
        EASING_KEY.SPIN_OUT,
        this.physics.scheduledFinalizeDurationMs
      )
    }
  }

  private startTricksSectorColorTweenIfNeeded(): void {
    if (!userParticipatesInWheelParties(this.parties, this.currentUserId)) {
      return
    }
    const targetParties = applyTricksPhaseSectorColors(
      this.parties,
      this.currentUserId
    )
    this.startSectorColorTween(this.parties, targetParties)
  }

  /** TRICKS → WAITING/BETTING: smooth revert to default palette (same gate as tricks tween). */
  private startBettingPaletteColorTweenIfNeeded(
    previousParties: SectorParty[],
    targetParties: SectorParty[],
    status?: string
  ): boolean {
    if (status !== "WAITING" && status !== "BETTING") return false
    if (!userParticipatesInWheelParties(previousParties, this.currentUserId)) {
      return false
    }
    if (!partiesUseTricksPalette(previousParties)) return false
    if (
      previousParties.length === 0 ||
      previousParties.length !== targetParties.length
    ) {
      return false
    }
    return this.startSectorColorTween(previousParties, targetParties)
  }

  /** Weight tween pauses idle spin; color-only tween must not freeze the wheel. */
  private sectorTweenBlocksIdleSpin(): boolean {
    const tween = this.sectorTween
    if (!tween) return false
    return !this.areSectorAngleLayoutsEqual(tween.fromAngles, tween.toAngles)
  }

  private startSectorColorTween(
    fromParties: SectorParty[],
    targetParties: SectorParty[]
  ): boolean {
    if (wheelPartiesColorsEqual(fromParties, targetParties)) return false

    const angles = computeSectorStartAngles(
      targetParties.length > 0 ? targetParties : fromParties
    )
    const now = this.clock.perfNow()
    this.physics.lastTickTime = now
    this.sectorTween = {
      fromAngles: angles,
      toAngles: angles,
      fromParties: fromParties.map((p) => ({ ...p })),
      parties: targetParties,
      durationMs: getPvpRouletteRuntime().TRICKS_SECTOR_COLOR_TWEEN_MS,
      easing: getEasing(EASING_KEY.EASE_OUT_CUBIC),
      startTime: now,
    }
    this.tick()
    return true
  }

  private tickSectorTween(now: number): void {
    if (!this.sectorTween) return
    const {
      fromAngles,
      toAngles,
      parties,
      fromParties,
      durationMs,
      easing,
      startTime,
    } = this.sectorTween
    const t = Math.min(1, (now - startTime) / durationMs)
    const e = easing(t)
    const len = Math.max(parties.length + 1, fromAngles.length, toAngles.length)
    const fa = this.padSectorAnglesForLerp(fromAngles, len, parties)
    const ta = this.padSectorAnglesForLerp(toAngles, len, parties)
    const angles = fa.map((a, i) => a + ((ta[i] ?? a) - a) * e)
    const displayParties =
      fromParties != null
        ? parties.map((p, i) => {
            const from = fromParties[i]
            if (!from) return p
            return {
              ...p,
              color: lerpSectorColor(from.color ?? 0, p.color ?? 0, e),
            }
          })
        : parties
    const forceLayoutLog = t < 0.05 || t >= 0.998
    this.scene?.drawSectorsFromAngles(angles, displayParties, {
      tweenT: t,
      forceLayoutLog,
    })
    if (t >= 1) {
      this.parties = parties
      this.sectorTween = null
      // Rebuild the pooled mesh once at rest so sector count / pool always matches `this.parties`
      // (avoids relying on the last tween frame alone after container or pattern races).
      this.scene?.drawSectors(this.parties)
    }
  }

  private tickRender(
    now: number,
    nowMs: number,
    angle: number,
    trickOffset: number,
    physicsNeedsTick: boolean
  ): void {
    const visualAngle = angle + trickOffset

    const dtMs = now - this.physics.lastTickTime
    if (this.onDiagnostics && dtMs >= 1) {
      const speedDegPerSec =
        (Math.abs(visualAngle - this.prevRotationDeg) / dtMs) * 1000
      const relTs = now - this.initTime
      if (relTs - this.diagLastSpeedTs >= 50) {
        this.diagLastSpeedTs = relTs
        this.onDiagnostics({
          type: "speed",
          ts: relTs,
          wallTimeMs: nowMs,
          speedDegPerSec,
          currentRotationDeg: visualAngle,
          dtMs,
        })
      }
    }
    this.prevRotationDeg = visualAngle
    this.physics.lastTickTime = now

    this.scene?.render(visualAngle)

    const needsTick = this.sectorTween != null || physicsNeedsTick
    if (needsTick) {
      this.scheduleWheelTick()
    }
  }
}
